import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const AdminLegal: React.FC = () => {
    const { token } = useAuth();
    
    const [privacyPolicy, setPrivacyPolicy] = useState('');
    const [termsConditions, setTermsConditions] = useState('');
    const [homeDisclaimer, setHomeDisclaimer] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const data = await api.getSystemSettings();
                if (data && !data.message) {
                    setPrivacyPolicy(data.privacyPolicy || '');
                    setTermsConditions(data.termsConditions || '');
                    setHomeDisclaimer(data.homeDisclaimer || '');
                }
            } catch (err) {
                console.error('Failed to fetch legal settings:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchSettings();
    }, []);

    const handleSave = async () => {
        if (!token) return;
        setSaving(true);
        try {
            const data = await api.updateSystemSettings(token, {
                privacyPolicy,
                termsConditions,
                homeDisclaimer
            });
            if (data && !data.message) {
                toast.success('Legal pages updated successfully');
            } else {
                toast.error(data.message || 'Failed to update settings');
            }
        } catch (err) {
            console.error('Failed to update legal settings:', err);
            toast.error('An error occurred while saving settings');
        } finally {
            setSaving(false);
        }
    };

    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
            ['link', 'image'],
            ['clean'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'align': [] }],
        ],
    };

    return (
        <AdminLayout>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                <div>
                    <h3 className="text-3xl font-bold text-slate-900 dark:text-white leading-none">
                        Legal <span className="text-[#05144b]">& Compliance</span>
                    </h3>
                    <p className="text-slate-500 dark:text-slate-400 font-medium text-sm mt-3">
                        Manage Privacy Policy and Terms of Service content.
                    </p>
                </div>
                <button 
                    onClick={handleSave}
                    disabled={saving}
                    className={`px-8 py-3.5 rounded-xl text-sm font-bold tracking-wider transition-all transform active:scale-95 flex items-center justify-center gap-3 ${saving ? 'bg-slate-400 cursor-not-allowed text-white' : 'bg-[#05144b] text-white hover:bg-blue-900 shadow-xl shadow-blue-900/20'}`}
                >
                    {saving ? (
                        <>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Saving Changes...</span>
                        </>
                    ) : (
                        <>
                            <i className="fa-solid fa-cloud-arrow-up text-lg"></i>
                            <span>Publish Legal Content</span>
                        </>
                    )}
                </button>
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center py-20 bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm">
                    <div className="w-12 h-12 border-4 border-[#05144b] border-t-transparent rounded-full animate-spin mb-4"></div>
                    <p className="text-slate-400 font-medium text-sm">Loading CMS Module...</p>
                </div>
            ) : (
                <div className="space-y-12 mb-20">
                    {/* Privacy Policy */}
                    <div className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-2xl shadow-blue-900/5 overflow-hidden">
                        <div className="p-6 border-b border-slate-50 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-blue-50 text-blue-600">
                                <i className="fa-solid fa-shield-halved text-lg"></i>
                            </div>
                            <h4 className="text-lg font-bold text-slate-900 dark:text-white">Privacy Policy</h4>
                        </div>
                        <div className="p-0 admin-quill-legal">
                            <ReactQuill
                                theme="snow"
                                value={privacyPolicy}
                                onChange={setPrivacyPolicy}
                                modules={quillModules}
                                placeholder="Write your Privacy Policy here..."
                                className="min-h-[400px]"
                            />
                        </div>
                    </div>

                    {/* Terms and Conditions */}
                    <div className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-2xl shadow-blue-900/5 overflow-hidden">
                        <div className="p-6 border-b border-slate-50 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-amber-50 text-amber-600">
                                <i className="fa-solid fa-file-contract text-lg"></i>
                            </div>
                            <h4 className="text-lg font-bold text-slate-900 dark:text-white">Terms and Conditions</h4>
                        </div>
                        <div className="p-0 admin-quill-legal">
                            <ReactQuill
                                theme="snow"
                                value={termsConditions}
                                onChange={setTermsConditions}
                                modules={quillModules}
                                placeholder="Write your Terms and Conditions here..."
                                className="min-h-[400px]"
                            />
                        </div>
                    </div>

                    {/* Home Page Disclaimer */}
                    <div className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-2xl shadow-blue-900/5 overflow-hidden">
                        <div className="p-6 border-b border-slate-50 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-rose-50 text-rose-600">
                                <i className="fa-solid fa-circle-exclamation text-lg"></i>
                            </div>
                            <h4 className="text-lg font-bold text-slate-900 dark:text-white">Homepage Disclaimer Popup</h4>
                        </div>
                        <div className="p-6">
                            <textarea
                                value={homeDisclaimer}
                                onChange={(e) => setHomeDisclaimer(e.target.value)}
                                placeholder="Write the disclaimer text that appears as a popup on the home page. You can use multiple paragraphs..."
                                className="w-full min-h-[300px] p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 focus:bg-white dark:focus:bg-slate-800 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm leading-relaxed resize-y"
                            ></textarea>
                            <p className="text-xs text-slate-500 mt-2">
                                This text is displayed in the popup that appears when a user first visits the home page. Leave empty if you do not want a popup, or add paragraphs.
                            </p>
                        </div>
                    </div>

                    <style>{`
                        .admin-quill-legal .ql-toolbar.ql-snow {
                            border: none !important;
                            padding: 1.25rem !important;
                            background: #f8fafc !important;
                            border-bottom: 1px solid #f1f5f9 !important;
                        }
                        .dark .admin-quill-legal .ql-toolbar.ql-snow {
                            background: #1e293b !important;
                            border-bottom: 1px solid #334155 !important;
                        }
                        .admin-quill-legal .ql-container.ql-snow {
                            border: none !important;
                            font-size: 0.95rem !important;
                            min-height: 400px;
                        }
                        .admin-quill-legal .ql-editor {
                            padding: 2rem !important;
                            min-height: 400px;
                        }
                        .dark .ql-snow .ql-stroke { stroke: #94a3b8 !important; }
                        .dark .ql-snow .ql-fill { fill: #94a3b8 !important; }
                        .dark .ql-snow .ql-picker { color: #94a3b8 !important; }
                        .dark .ql-editor { color: #e2e8f0 !important; }
                    `}</style>
                </div>
            )}
        </AdminLayout>
    );
};

export default AdminLegal;
